/**
 * One document in Home's Findings tab: a row that folds its first findings
 * under it, with its critical mark and how many it holds, and a link to the
 * document for the rest.
 *
 * @module components/home/HomeFindingGroup
 */
import { Link } from "@tanstack/react-router";
import { FileQuestionIcon, ListChecksIcon, ScrollTextIcon, type LucideIcon } from "lucide-react";
import { useState } from "react";

import { Collapsible, CollapsibleTrigger } from "../ui/collapsible";
import { ListRow, ListRowIcon, ListRowTitle } from "../ui/list-row";
import { ListSectionChevron, ListSectionPanel } from "../ui/list-section";
import { Tip } from "../ui/tip";
import { SeverityGlyph } from "../regulation/finding/findingGlyphs";
import type { HomeDocumentKind, HomeFindingGroup as FindingGroup } from "./HomeFindings.logic";
import { HomeFindingRow } from "./HomeFindingRow";
import { FINDING_GROUP_ROWS, findingGroupBreakdown } from "./homeTabs.logic";

/** The icons Governance's menu gives policies and procedures. */
const DOCUMENT_ICON: Readonly<Record<HomeDocumentKind, LucideIcon>> = {
  policy: ScrollTextIcon,
  procedure: ListChecksIcon,
  missing: FileQuestionIcon,
};

export function HomeFindingGroup({
  group,
  orgSegment,
  now,
  defaultOpen,
}: {
  readonly group: FindingGroup;
  readonly orgSegment: string;
  readonly now: number;
  readonly defaultOpen: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const more = group.items.length - FINDING_GROUP_ROWS;
  const DocumentIcon = DOCUMENT_ICON[group.documentKind];
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <ListRow render={<CollapsibleTrigger />}>
        <ListRowIcon>
          <ListSectionChevron open={open} />
        </ListRowIcon>
        <ListRowIcon>
          <DocumentIcon />
        </ListRowIcon>
        <ListRowTitle>{group.documentName}</ListRowTitle>
        {/* Red only where it means something: the critical mark, and one total whose breakdown is in its tooltip. */}
        {group.counts.critical > 0 ? <SeverityGlyph severity="critical" tone="quiet" className="size-3.5" /> : null}
        <Tip label={findingGroupBreakdown(group)}>
          <span className="min-w-4 shrink-0 text-right text-xs tabular-nums text-muted-foreground">{group.items.length}</span>
        </Tip>
      </ListRow>
      <ListSectionPanel headerIcons={2}>
        <ul>
          {group.items.slice(0, FINDING_GROUP_ROWS).map((item) => (
            <HomeFindingRow key={item.id} item={item} orgSegment={orgSegment} now={now} />
          ))}
          {more > 0 ? (
            <li>
              {/* A missing document has no page of its own: the rest wait in the findings queue. */}
              {group.policyId ? (
                <Link
                  to="/$org/compliance/policies/$policyId"
                  params={{ org: orgSegment, policyId: group.policyId }}
                  hash="findings"
                  className="flex min-h-7 items-center ps-[30px] text-xs text-muted-foreground hover:text-foreground"
                >
                  {more} more
                </Link>
              ) : (
                <Link
                  to="/$org/compliance/findings"
                  params={{ org: orgSegment }}
                  className="flex min-h-7 items-center ps-[30px] text-xs text-muted-foreground hover:text-foreground"
                >
                  {more} more
                </Link>
              )}
            </li>
          ) : null}
        </ul>
      </ListSectionPanel>
    </Collapsible>
  );
}
